/*
  Product cards (snippets/card-product.liquid). A picked swatch shows its color's photo and points the card's link and
  quick add at that variant. The image carousel builds its slides from the card's list of photos the first time a
  shopper reaches for it (pointer, focus or an arrow), from 769 px only, and steps through the picked color's photos:
  each swatch photo starts a run that ends where the next one starts. Photo files load only when shown or next.
*/

if (!customElements.get('product-card')) {
  // The width from which the image carousel steps: one query for every card, made when the first needs it.
  let largeQuery;
  const wide = () => (largeQuery ??= windowFrom('large'));

  class ProductCard extends HTMLElement {
    // As snippets/image.liquid scales its files.
    static quality = { lighter: 0.75, sharper: 1.25 };

    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.track = this.querySelector('.product-card__track');
      this.arrows = this.querySelector('.product-card__arrows');
      this.index = 0;
      this.swaps = 0;

      this.addEventListener('change', (event) => this.pick(event.target), { signal });
      if (!this.track || !this.arrows) return;

      this.arrows.hidden = false;
      this.arrows.addEventListener(
        'click',
        (event) => {
          const arrow = event.target.closest('[data-photo-step]');
          if (arrow) this.step(Number(arrow.dataset.photoStep));
        },
        { signal }
      );
      this.addEventListener('pointerenter', () => this.build(), { signal, passive: true });
      this.addEventListener('focusin', () => this.build(), { signal });
      // Narrowed to a phone after the slides were built: back to the run's first photo, as phones show one.
      wide().addEventListener(
        'change',
        () => {
          if (wide().matches || !this.slides) return;
          this.index = 0;
          this.move();
        },
        { signal }
      );
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    get photos() {
      if (!this.photoList) {
        try {
          this.photoList = JSON.parse(this.querySelector('[data-card-images]')?.textContent || '[]');
        } catch {
          this.photoList = [];
        }
      }
      return this.photoList;
    }

    get mainImage() {
      return this.querySelector('.product-card__frame .product-card__image:not(.product-card__image--next)');
    }

    // An <img> for a listed photo, sized like the card's first image, without upscaling the file. Theme settings ›
    // Advanced › Image quality scales its files as snippets/image.liquid scales the first image's.
    draw(photo, model) {
      const frame = this.querySelector('.product-card__frame');
      const scale = ProductCard.quality[frame?.dataset.quality] ?? 1;
      const boxRatio = Number(frame?.dataset.boxRatio) || 1;
      const fill = model.classList.contains('fit-cover');
      // A photo cropped to fill the box needs a wider file, by the share the crop cuts away.
      const crop = fill ? Math.max(1, photo.w / photo.h / boxRatio) : 1;
      const factor = scale * crop;
      const steps = (model.getAttribute('srcset') || '')
        .split(',')
        .map((entry) => parseInt(entry.trim().split(/\s+/)[1], 10))
        .filter((width) => width > 0);
      const largest = Math.min(Math.max(...steps, 360), photo.w);
      const url = (width) => `${photo.src}${photo.src.includes('?') ? '&' : '?'}width=${width}`;
      const candidates = steps
        .filter((width) => width < largest && Math.round(width * factor) < photo.w)
        .map((width) => `${url(Math.round(width * factor))} ${width}w`);
      candidates.push(`${url(Math.min(Math.round(largest * factor), photo.w))} ${largest}w`);

      const image = document.createElement('img');
      image.className = model.className;
      image.src = url(Math.min(Math.round(largest * factor), photo.w));
      image.srcset = candidates.join(', ');
      image.sizes = model.sizes;
      image.width = photo.w;
      image.height = photo.h;
      image.alt = photo.alt || '';
      image.loading = 'lazy';
      image.decoding = 'async';
      if (fill && photo.f) image.style.objectPosition = photo.f;
      return image;
    }

    pick(input) {
      if (!input.matches('.product-card__swatches input')) return;
      this.toggleAttribute('data-picked', true);

      const url = input.dataset.variantUrl;
      if (url) {
        const link = this.querySelector('.product-card__title a');
        if (link) link.href = url;
        this.querySelector('[data-quick-add]')?.setAttribute('data-quick-add', url);
      }

      const photo = this.photoOf(input);
      if (!photo) return;
      if (this.slides) {
        this.show(photo.id);
      } else {
        if (this.track) this.track.dataset.currentPhoto = photo.id;
        this.swap(photo);
      }
    }

    // A swatch's photo (id, file, size, alt text and focal point): the photo itself, or its id in the card's list.
    photoOf(input) {
      if (!input.dataset.photo) return null;
      try {
        const value = JSON.parse(input.dataset.photo);
        return typeof value === 'number' ? (this.photos.find((photo) => photo.id === value) ?? null) : value;
      } catch {
        return null;
      }
    }

    // Shows a photo in place of the first one once it is ready to paint, so the card never flashes empty; a photo that
    // fails to load leaves the current one. Only the latest pick is placed.
    swap(photo) {
      const model = this.mainImage;
      if (!model) return;
      const token = ++this.swaps;
      const next = this.draw(photo, model);
      next.loading = 'eager';
      next
        .decode()
        .then(() => {
          if (token === this.swaps) this.mainImage?.replaceWith(next);
        })
        .catch(() => {});
    }

    build() {
      if (this.slides || !this.track || !wide().matches) return;
      const first = this.track.querySelector('img');
      if (!first || this.photos.length < 2) return;

      const current = Number(this.track.dataset.currentPhoto);
      this.slides = this.photos.map((photo) => (photo.id === current ? first : this.draw(photo, first)));
      this.track.replaceChildren(...this.slides);
      // Each swatch photo starts a color's run of photos.
      const ids = [...this.querySelectorAll('.product-card__swatches input[data-photo]')].map(
        (input) => this.photoOf(input)?.id
      );
      this.starts = this.photos
        .map((photo, index) => (ids.includes(photo.id) ? index : -1))
        .filter((index) => index >= 0);
      this.show(current);
    }

    // Shows the run of photos a color starts (or every photo) from the given one.
    show(id) {
      if (!this.slides) return;
      const at = Math.max(
        0,
        this.photos.findIndex((photo) => photo.id === id)
      );
      let from = 0;
      let to = this.slides.length;
      if (this.starts.includes(at)) {
        from = at;
        to = this.starts.find((start) => start > at) ?? this.slides.length;
      }
      this.slides.forEach((slide, index) => {
        slide.hidden = index < from || index >= to;
      });
      this.index = at - from;
      this.move();
    }

    step(direction) {
      this.build();
      if (!this.slides) return;
      const count = this.slides.filter((slide) => !slide.hidden).length;
      this.index = Math.min(Math.max(this.index + direction, 0), count - 1);
      this.move();
    }

    move() {
      const visible = this.slides.filter((slide) => !slide.hidden);
      this.track.style.setProperty('--slide', this.index);
      visible.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== this.index));
        if (index === this.index + 1) slide.loading = 'eager';
      });

      const [previous, next] = this.arrows.querySelectorAll('[data-photo-step]');
      const focused = this.arrows.contains(document.activeElement) ? document.activeElement : null;
      previous.disabled = this.index === 0;
      next.disabled = this.index >= visible.length - 1;
      // A disabled button drops the focus: hand it to the other arrow.
      if (focused?.disabled) (focused === next ? previous : next).focus();
    }
  }

  customElements.define('product-card', ProductCard);
}
